import {
  localization,
  SurveyQuestionEditorDefinition,
} from 'survey-creator-react';
import { ElementFactory, Question, Serializer } from 'survey-core';
import {
  SurveyQuestionElementBase,
  ReactQuestionFactory,
} from 'survey-react-ui';

import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import { findSentimentColor } from './sentimentColors';
import { Button } from '@material-ui/core';

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.13);
  z-index: 1000;
`;
const Content = styled.div`
  display: flex;
  width: 50rem;
  padding: 2rem;
  background-color: white;
  flex-direction: column;
  border-radius: 0.5rem;
  box-shadow: 0px 1px 2px rgb(0 0 0 / 15%);
`;

const Footer = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: space-between;
`;

const Table = styled.table`
  border-collapse: collapse;

  Td,
  tr,
  Th {
    border: 1px solid black;
    text-align: center;
    position: relative;
  }
`;
const Td = styled.td`
  padding: 2px 4px !important;
`;

const Th = styled.th`
  padding: 2px 4px !important;
`;

const Input = styled.input`
  border: none;
  outline: none;
  border-bottom: 1px solid #b7b7b7;
`;

const StyledButton = styled(Button)`
  background-color: white;
  color: black;
  border-color: #d3d3d3;
  &:hover {
    background-color: white;
    border-color: #19b394;
  }
`;

const SentimentLabelEditor = ({
  observer,
  sentimentsSet,
  saveValue,
  value,
}: any) => {
  const [sentiments, setSentiments] = useState(sentimentsSet || []);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [sentimentTranslations, setTranslations] = useState(
    sentiments.reduce(function (acc: any, curr: any) {
      const isValueExist = value && value[curr];

      acc[curr] = {};
      acc[curr].en = isValueExist ? value[curr].en : '';
      acc[curr].de = isValueExist ? value[curr].de : '';
      acc[curr].fr = isValueExist ? value[curr].fr : '';
      acc[curr].it = isValueExist ? value[curr].it : '';

      return acc;
    }, {})
  );

  useEffect(() => {
    observer.add((newData: any) => {
      const changedSentiments: string[] = [];

      newData.activeChoices.forEach((itemValue: any) => {
        const { sentiment } = itemValue;

        const isAdded = changedSentiments.includes(sentiment);

        if (!isAdded && sentiment) {
          changedSentiments.push(sentiment);
        }
      });

      const newSentiments: any = {};

      changedSentiments.forEach((changedSentiment: string) => {
        const isNew = !sentiments.includes(changedSentiment);
        if (isNew) {
          newSentiments[changedSentiment] = {};
          newSentiments[changedSentiment].en = '';
          newSentiments[changedSentiment].de = '';
          newSentiments[changedSentiment].fr = '';
          newSentiments[changedSentiment].it = '';
        }
      });

      setTranslations({
        ...sentimentTranslations,
        ...newSentiments,
      });
      setSentiments(changedSentiments);
    });
    // eslint-disable-next-line
  }, []);

  const changeTranslation = (
    sentimentValue: string,
    locale: string,
    label: string
  ) => {
    const translations = { ...sentimentTranslations };
    translations[sentimentValue][locale] = label;

    setTranslations(translations);
  };

  const saveSentimentMap = () => {
    saveValue(sentimentTranslations);
    setIsEditorOpen(false);
  };

  const sortedSentiments = sentiments.sort((a: number, b: number) => a - b);
  return (
    <div>
      <StyledButton
        variant="outlined"
        size="small"
        color="secondary"
        onClick={() => setIsEditorOpen(true)}
      >
        Edit Sentiment labels
      </StyledButton>
      {isEditorOpen && (
        <Wrapper>
          <Content>
            <Table>
              <thead>
                <tr>
                  <Th rowSpan={2} />
                  <Th rowSpan={2}>Sentiment</Th>
                  <Th style={{ textAlign: 'center' }} colSpan={4}>
                    Translation
                  </Th>
                </tr>
                <tr>
                  <Th>EN</Th>
                  <Th>DE</Th>
                  <Th>FR</Th>
                  <Th>IT</Th>
                </tr>
              </thead>
              <tbody>
                {sortedSentiments.map((sentimentValue: string) => {
                  const color = findSentimentColor(sentimentValue);

                  return (
                    <tr key={sentimentValue}>
                      <Td
                        style={{ backgroundColor: color, width: '1rem' }}
                      ></Td>
                      <Td>{sentimentValue}</Td>
                      <Td>
                        <Input
                          type="text"
                          value={sentimentTranslations[sentimentValue].en}
                          onChange={(e: any) =>
                            changeTranslation(
                              sentimentValue,
                              'en',
                              e.target.value
                            )
                          }
                        />
                      </Td>
                      <Td>
                        <Input
                          type="text"
                          value={sentimentTranslations[sentimentValue].de}
                          onChange={(e: any) =>
                            changeTranslation(
                              sentimentValue,
                              'de',
                              e.target.value
                            )
                          }
                        />
                      </Td>
                      <Td>
                        <Input
                          type="text"
                          value={sentimentTranslations[sentimentValue].fr}
                          onChange={(e: any) =>
                            changeTranslation(
                              sentimentValue,
                              'fr',
                              e.target.value
                            )
                          }
                        />
                      </Td>
                      <Td>
                        <Input
                          type="text"
                          value={sentimentTranslations[sentimentValue].it}
                          onChange={(e: any) =>
                            changeTranslation(
                              sentimentValue,
                              'it',
                              e.target.value
                            )
                          }
                        />
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            <Footer>
              <StyledButton
                variant="outlined"
                size="small"
                onClick={saveSentimentMap}
              >
                Save
              </StyledButton>

              <StyledButton
                variant="outlined"
                size="small"
                onClick={() => setIsEditorOpen(false)}
              >
                Cancel
              </StyledButton>
            </Footer>
          </Content>
        </Wrapper>
      )}
    </div>
  );
};

// create model for new question type
export class QuestionSentimentEditorModel extends Question {
  getType() {
    return 'sentimentEditor';
  }
  get sentimentEditorType() {
    return this.getPropertyValue('sentimentEditorType');
  }
  set sentimentEditorType(val) {
    this.setPropertyValue('sentimentEditorType', val);
  }
}

// set display title for question type
localization.getLocale('').qt.sentimentEditor = 'Sentiment Editor';

// this serializes the class into JSON
Serializer.addClass(
  'sentimentEditor',
  [
    {
      name: 'sentimentEditorType',
    },
  ],
  function () {
    return new QuestionSentimentEditorModel('');
  },
  'question'
);

SurveyQuestionEditorDefinition.definition.sentiment = {
  properties: [{ name: 'sentimentEditorType', tab: 'layout' }],
};

// register question with its model
export function registerSentimentEditor() {
  ElementFactory.Instance.registerElement('sentimentEditor', (name: string) => {
    return new QuestionSentimentEditorModel(name);
  });
}

export class SurveyQuestionSentimentEditor extends SurveyQuestionElementBase {
  constructor(props: any) {
    super(props);
    const sentimentsSet: string[] = [];

    props.question.obj.activeChoices &&
      props.question.obj.activeChoices.forEach((itemValue: any) => {
        const { sentiment } = itemValue;

        const isAdded = sentimentsSet.includes(sentiment);

        if (!isAdded && sentiment) {
          sentimentsSet.push(sentiment);
        }
      });

    this.state = {
      value: this.question.value,
      sentimentsSet: sentimentsSet,
    };
  }
  get question() {
    return this.questionBase;
  }
  get value() {
    return this.question.value;
  }

  get type() {
    // @ts-ignore
    return this.question.sentimentEditorType;
  }
  handleSentimentEditorChange = (data: any) => {
    this.question.value = data;
    this.setState({ value: data });
  };

  // support readOnly and designMode
  get style() {
    return this.question.getPropertyValue('readOnly') ||
      this.question.isDesignMode
      ? {
          pointerEvents: 'none',
        }
      : undefined;
  }

  renderElement() {
    return (
      <SentimentLabelEditor
        saveValue={this.handleSentimentEditorChange}
        value={this.state.value}
        observer={this.props.question.obj.onItemValuePropertyChanged}
        sentimentsSet={this.state.sentimentsSet}
      />
    );
  }
}

ReactQuestionFactory.Instance.registerQuestion(
  'sentimentEditor',
  (props: any) => {
    return React.createElement(SurveyQuestionSentimentEditor, props);
  }
);
